"use client";
import {
  Container,
  createTheme,
  Loader,
  MantineColorsTuple,
  MantineProvider,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { getCoreThemeColors } from "@utils/helpers";
import { SessionProvider } from "next-auth/react";
import { NextFont } from "next/dist/compiled/@next/font";
import { useMemo, useState } from "react";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { store } from "../store";
import RefreshTokenHandler from "./RefreshTokenHandler";

const persistor = persistStore(store);

const CONTAINER_SIZES: Record<string, string> = {
  xs: "540px",
  sm: "720px",
  md: "960px",
  lg: "1140px",
  xl: "1320px",
};

export default function Providers({
  children,
  font,
  headerFont,
}: {
  children: React.ReactNode;
  font: NextFont;
  headerFont: NextFont;
}) {
  const [authInterval, setAuthInterval] = useState(0);

  const theme = useMemo(() => {
    const rawColors = getCoreThemeColors();
    const colors = Object.fromEntries(
      Object.entries(rawColors).map(([key, value]) => [
        key,
        value as unknown as MantineColorsTuple,
      ])
    );

    return createTheme({
      fontFamily: font.style.fontFamily,
      fontSizes: {
        xs: "0.9rem",
        sm: "1rem",
        md: "1.1rem",
        lg: "1.3rem",
        xl: "1.6rem",
      },
      headings: {
        fontFamily: headerFont.style.fontFamily,
        sizes: {
          h1: { fontSize: "2.4rem", fontWeight: "800" },
          h2: { fontSize: "2rem", fontWeight: "800" },
          h3: { fontSize: "1.7rem", fontWeight: "750" },
          h4: { fontSize: "1.5rem", fontWeight: "750" },
          h5: { fontSize: "1.4rem", fontWeight: "700" },
          h6: { fontSize: "1.2rem", fontWeight: "700" },
        },
      },
      radius: {
        xs: "0.15em",
        sm: "0.25em",
        md: "0.5em",
        lg: "1em",
        xl: "10em",
      },
      spacing: {
        xs: "0.25em",
        sm: "0.5em",
        md: "0.75em",
        lg: "1em",
        xl: "1.5em",
      },
      cursorType: "pointer",
      defaultGradient: { deg: 180, from: "lightblue", to: "blue" },
      primaryColor: "blue",
      primaryShade: 5,
      colors,
      components: {
        Container: Container.extend({
          vars: (_, { size, fluid }) => ({
            root: {
              "--container-size":
                fluid || !size || typeof size === "number"
                  ? undefined
                  : CONTAINER_SIZES[size] ?? undefined,
            },
          }),
        }),
        Loader: Loader.extend({
          defaultProps: { type: "bars" },
        }),
      },
    });
  }, [font, headerFont]);

  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications />
      <ModalsProvider>
        <SessionProvider refetchInterval={authInterval}>
          <Provider store={store}>
            <RefreshTokenHandler setAuthInterval={setAuthInterval} />
            {children}
          </Provider>
        </SessionProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}
